import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { StatusPagePiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'

export const Route = createFileRoute('/_site/for/product-teams')({
  component: ProductTeamsPage,
  head: () => ({
    meta: [
      { title: 'Uptime Monitoring for Product Teams' },
      { name: 'description', content: 'Product-team visibility into uptime, response times, incidents: timelines, status pages, and on paid plans weekly reports and exports.' },
      { property: 'og:title', content: 'Uptime Monitoring for Product Teams — Reports | Uptrack' },
      { property: 'og:description', content: 'Uptime dashboards, weekly reports, incident timelines, and data export. Built for product teams. Free plan, or paid plans from $3/mo.' },
      { property: 'og:image', content: 'https://uptrack.app/og/for-product-teams.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/product-teams' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'For Product Teams', item: 'https://uptrack.app/for/product-teams' },
          ],
        }),
      },
    ],
  }),
})

function ProductTeamsPage() {
  return (
    <FeaturePage
      eyebrow='For · Product teams'
      section='Product teams'
      title='Show your team the numbers that matter.'
      intro='Uptime percentage, response times, incident history — all in one dashboard. On paid plans, weekly reports go to stakeholders automatically and you can export data when you need it. Everyone stays informed without asking engineering.'
      featuresHeading='Visibility for the whole team'
      features={[
        { title: 'Dashboard overview', desc: 'See uptime percentages and response times across all monitors at a glance. No digging through logs.' },
        { title: 'Weekly reports', desc: "Automated weekly summaries delivered to your inbox (paid plans). Share with stakeholders who don't check dashboards." },
        { title: 'Incident timeline', desc: 'Every incident tracked with start time, duration, root cause, and resolution. Perfect for post-mortems.' },
        { title: 'Status pages for stakeholders', desc: 'Internal or public status pages. Keep leadership, support, and customers informed without manual updates.' },
        { title: 'Data export (CSV)', desc: 'Export uptime data, response times, and incident history on paid plans. Bring the numbers to your quarterly review.' },
        { title: 'Audit logs', desc: 'Track who created, modified, or deleted monitors (paid plans). Full audit trail for compliance and accountability.' },
      ]}
      why={{ heading: 'Your whole team sees the same status', body: "Your PM asks about last week's uptime. Your VP wants incident reports for the board. Support needs to know if the API is down right now. Instead of pinging engineers, everyone checks the dashboard or reads the weekly report. The data is always there." }}
      cta={{ heading: '30-second checks on every plan', body: 'Dashboards come with every plan. Reports, exports, and audit logs are on paid plans. Start free, or choose a plan for more.' }}
      visual={
        <ProductFrame url="status.example.com">
          <StatusPagePiece />
        </ProductFrame>
      }
    />
  )
}
